/**
 * The app's run modes (BOS-236). A mode fixes the build flavour and the API:
 *
 * | Mode          | Build       | API                          |
 * | ------------- | ----------- | ---------------------------- |
 * | `local`       | development | the laptop API on :5053      |
 * | `development` | development | `https://dev-api.bos.plus/`  |
 * | `production`  | production  | `https://api.bos.plus/`      |
 *
 * Each mode reads its own env file (`.env.<mode>`), fed with Bun's
 * `--env-file`, and the file names its mode in `BUN_PUBLIC_BOS_MODE`. The dev
 * server and `build.ts` refuse a file that is missing or names another mode
 * (`checkModeEnv`), so a laptop can never silently talk to the wrong API.
 *
 * Pure: shared by the Bun build scripts and the browser bundle. The running
 * bundle's own values come from `config/env`, the only browser reader of
 * `process.env`.
 */
export const APP_MODES = ['local', 'development', 'production'] as const;

export type AppMode = (typeof APP_MODES)[number];

export const isAppMode = (value: unknown): value is AppMode =>
    typeof value === 'string' &&
    (APP_MODES as readonly string[]).includes(value);

/** `production` builds as production; `local` and `development` as development. */
export const nodeEnvForMode = (mode: AppMode): 'production' | 'development' =>
    mode === 'production' ? 'production' : 'development';

export type ModeEnvCheckInput = {
    /** The mode the command runs, e.g. `local` for `bun start`. */
    expectedMode: AppMode;
    /** The variables Bun loaded from the env file (plus the shell's). */
    env: Record<string, string | undefined>;
    /** The env file's name or path, for the messages. */
    envFile: string;
    /** Whether that file exists. Bun silently loads nothing for a missing one. */
    envFileExists: boolean;
};

export type ModeEnvCheck =
    | { ok: true; mode: AppMode; apiBaseUrl: string }
    | { ok: false; message: string };

const copyHint = (mode: AppMode): string =>
    `Copy .env.${mode}.example to .env.${mode} and fill it in.`;

/**
 * Whether the loaded env file belongs to the mode the command runs. Refuses a
 * missing file, a file without a mode (such as a `.env.local` from before run
 * modes, which pointed at production), an unknown mode, a file for another
 * mode, and a missing or malformed API address.
 */
export const checkModeEnv = ({
    expectedMode,
    env,
    envFile,
    envFileExists,
}: ModeEnvCheckInput): ModeEnvCheck => {
    if (!envFileExists) {
        return {
            ok: false,
            message: `${envFile} is missing. ${copyHint(expectedMode)}`,
        };
    }

    const mode = env.BUN_PUBLIC_BOS_MODE?.trim();

    if (!mode) {
        return {
            ok: false,
            message:
                `${envFile} does not set BUN_PUBLIC_BOS_MODE (this command runs "${expectedMode}"). ` +
                copyHint(expectedMode),
        };
    }

    if (!isAppMode(mode)) {
        return {
            ok: false,
            message:
                `${envFile} sets BUN_PUBLIC_BOS_MODE="${mode}", which is not one of ` +
                `${APP_MODES.join(', ')}. ${copyHint(expectedMode)}`,
        };
    }

    if (mode !== expectedMode) {
        return {
            ok: false,
            message:
                `${envFile} is for "${mode}", but this command runs "${expectedMode}". ` +
                copyHint(expectedMode),
        };
    }

    const apiBaseUrl = env.BUN_PUBLIC_API_BASE_URL?.trim() ?? '';

    if (!apiBaseUrl) {
        return {
            ok: false,
            message: `${envFile} does not set BUN_PUBLIC_API_BASE_URL. ${copyHint(expectedMode)}`,
        };
    }

    if (!URL.canParse(apiBaseUrl)) {
        return {
            ok: false,
            message: `${envFile} sets BUN_PUBLIC_API_BASE_URL="${apiBaseUrl}", which is not a URL.`,
        };
    }

    return { ok: true, mode, apiBaseUrl };
};

export type ModeDisplay = {
    mode: AppMode;
    /** Whether the bundle is a development build (`local`, `development`). */
    isDevelopmentBuild: boolean;
    /** The sidebar badge, e.g. `DEV · dev-api.bos.plus`; `null` in production. */
    badgeLabel: string | null;
    /** The tab-title prefix, e.g. `[DEV]`; `null` in production. */
    titlePrefix: string | null;
};

const MODE_TAGS: Record<AppMode, string | null> = {
    local: 'LOCAL',
    development: 'DEV',
    production: null,
};

const hostOf = (apiBaseUrl: string): string =>
    URL.canParse(apiBaseUrl) ? new URL(apiBaseUrl).host : apiBaseUrl;

/**
 * What a mode shows. Anything that is not a known mode reads as production, so
 * a bundle built without a mode (a test, an old build) never shows developer
 * aids.
 */
export const getModeDisplay = (
    mode: string | undefined,
    apiBaseUrl: string,
): ModeDisplay => {
    const resolved: AppMode = isAppMode(mode) ? mode : 'production';
    const tag = MODE_TAGS[resolved];

    return {
        mode: resolved,
        isDevelopmentBuild: resolved !== 'production',
        badgeLabel: tag ? `${tag} · ${hostOf(apiBaseUrl)}` : null,
        titlePrefix: tag ? `[${tag}]` : null,
    };
};

/** `title` with the mode's tab prefix, e.g. `[DEV] Bos App`; unchanged in production. */
export const withModeTitlePrefix = (
    title: string,
    display: ModeDisplay,
): string => {
    const prefix = display.titlePrefix;

    if (!prefix || title.startsWith(`${prefix} `)) {
        return title;
    }

    return `${prefix} ${title}`;
};
